import { i18n } from 'boot/i18n';
import { uid } from 'quasar';
import { notifyNegative } from 'src/composables/interactions';
import { auth as useAuthStore } from 'src/store/auth';
import { clients as useClientsStore } from 'src/store/clients';
import { serializeFilters } from 'src/utils/FilterUtils';
import { computed, ref } from 'vue';

import { createCareModelCohorts } from '../../application/useCases/createCareModelCohorts';
import { unassignClientsFromCohort } from '../../infrastructure/api/careModelClientAssignmentsApi';
import { saveCareModelCohorts } from '../../infrastructure/api/careModelCohortsApi';
import { useCareModelReviewAssignments } from './useCareModelReviewAssignments';
import { useCohortsFiltersColumns } from './useCohortsFiltersColumns';

/** @typedef {
 *   import('../../domain/entities/CareModelCohort.types').CareModelCohort
 * } CareModelCohort */
/** @typedef {
 *   import('../../domain/entities/CareModelCohort.types').Client
 * } Client */
/** @typedef {
 *   import('../../domain/entities/CareModelCohort.types').CohortFilters
 * } CohortFilters */
/** @typedef {
 *   import('../../application/useCases/createCareModelCohorts.types').SaveCareModelCohortParams
 * } SaveCareModelCohortParams */
/** @typedef {
 *   import('../../application/useCases/createCareModelCohorts.types').DeleteCareModelCohortParams
 * } DeleteCareModelCohortParams */
/** @typedef {
 *   import('../../application/useCases/createCareModelCohorts.types').DeleteCareModelCohortResult
 * } DeleteCareModelCohortResult */
/** @typedef {
 *   import('../../application/useCases/createCareModelCohorts.types').ReorderCareModelCohortsParams
 * } ReorderCareModelCohortsParams */

// eslint-disable-next-line import/prefer-default-export
export function useCareModelCohorts() {
  const authStore = useAuthStore();
  const clientsStore = useClientsStore();

  const { columns } = useCohortsFiltersColumns();
  const { prepareReview } = useCareModelReviewAssignments();

  const isProcessing = ref(false);
  const isLoading = ref(false);
  const isRefreshingReview = ref(false);
  const countHeldDuringRefresh = ref(null);
  const cohorts = ref([]);
  const impactedClients = ref([]);

  const cohortsUseCase = createCareModelCohorts(
    saveCareModelCohorts,
    uid,
    serializeFilters,
    unassignClientsFromCohort,
  );

  const sortedCohorts = computed(() => cohortsUseCase.loadCohorts({
    storedCohorts: cohorts.value,
  }));

  const assignedClientsCountByCohort = computed(() => {
    return cohortsUseCase.getAssignedClientsCount(clientsStore.regularClients);
  });

  const settledClientsNeedingCohortReviewCount = computed(() => {
    return cohortsUseCase.getClientsNeedingCohortReviewCount({
      clients: clientsStore.regularClients,
      impactedClients: impactedClients.value,
    });
  });

  const clientsNeedingCohortReviewCount = computed(() => (
    countHeldDuringRefresh.value ?? settledClientsNeedingCohortReviewCount.value
  ));

  const clientsNeedingCohortReviewMessage = computed(() => i18n.tc(
    'settings.careModel.cohorts.unassignedClientsBannerMessage',
    clientsNeedingCohortReviewCount.value,
    { count: clientsNeedingCohortReviewCount.value },
  ));

  const suggestedCohortUpdatesCount = computed(() => {
    return impactedClients.value.length;
  });

  const hasCohorts = computed(() => sortedCohorts.value.length > 0);

  async function ensureClientsLoaded() {
    if (clientsStore.clients.length === 0) {
      await clientsStore.initializeClients();
    }
  }

  function refreshImpactedClients() {
    if (!cohorts.value.length || !clientsStore.regularClients.length) {
      impactedClients.value = [];
      return [];
    }

    const { suggestedAssignments } = prepareReview({
      clients: clientsStore.regularClients,
      cohorts: cohorts.value,
      columns: columns.value,
    });

    impactedClients.value = suggestedAssignments;

    return suggestedAssignments;
  }

  async function refreshReviewState() {
    isRefreshingReview.value = true;
    countHeldDuringRefresh.value = settledClientsNeedingCohortReviewCount.value;

    try {
      await clientsStore.initializeClients();
      refreshImpactedClients();
    } catch (err) {
      notifyNegative(i18n.t('common.errors.generic'));
    } finally {
      countHeldDuringRefresh.value = null;
      isRefreshingReview.value = false;
    }
  }

  async function fetchCohorts() {
    isLoading.value = true;

    try {
      await Promise.all([
        authStore.loadClinicSettings(),
        ensureClientsLoaded(),
      ]);

      cohorts.value = cohortsUseCase.loadCohorts({
        storedCohorts: authStore.careModelCohorts,
      });
      refreshImpactedClients();
    } catch (err) {
      notifyNegative(i18n.t('common.errors.generic'));
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * @param {CohortFilters} filters
   * @returns {string[]}
   */
  function getFilterLabels(filters = {}) {
    return cohortsUseCase.getFilterLabels(filters);
  }

  /**
   * @param {CareModelCohort[]} nextCohorts
   * @returns {void}
   */
  function setCohorts(nextCohorts = []) {
    cohorts.value = nextCohorts;
    authStore.setCareModelCohorts(nextCohorts);
    refreshImpactedClients();
  }

  /**
   * @param {SaveCareModelCohortParams} params
   * @returns {Promise<CareModelCohort[]>}
   */
  async function saveOneCohort(params) {
    isProcessing.value = true;
    try {
      return await cohortsUseCase.saveCohort(params);
    } finally {
      isProcessing.value = false;
    }
  }

  /**
   * @param {DeleteCareModelCohortParams} params
   * @returns {Promise<DeleteCareModelCohortResult>}
   */
  async function deleteOneCohort(params) {
    isProcessing.value = true;
    try {
      return await cohortsUseCase.deleteCohort(params);
    } finally {
      isProcessing.value = false;
    }
  }

  /**
   * @param {ReorderCareModelCohortsParams} params
   * @returns {Promise<CareModelCohort[]>}
   */
  async function reorderAllCohorts(params) {
    isProcessing.value = true;
    try {
      return await cohortsUseCase.reorderCohorts(params);
    } finally {
      isProcessing.value = false;
    }
  }

  async function reorderCohorts(updatedCohorts) {
    const previousCohorts = cohorts.value;

    try {
      const nextCohorts = await reorderAllCohorts({
        clinicId: authStore.userDetails.clinic,
        cohorts: updatedCohorts,
      });

      setCohorts(nextCohorts);
    } catch (err) {
      cohorts.value = previousCohorts;
      notifyNegative(i18n.t('common.errors.generic'));
    }
  }

  return {
    isLoading,
    isProcessing,
    isRefreshingReview,
    cohorts,
    sortedCohorts,
    assignedClientsCountByCohort,
    clientsNeedingCohortReviewCount,
    clientsNeedingCohortReviewMessage,
    impactedClients,
    suggestedCohortUpdatesCount,
    hasCohorts,
    ensureClientsLoaded,
    fetchCohorts,
    refreshImpactedClients,
    refreshReviewState,
    saveOneCohort,
    deleteOneCohort,
    reorderCohorts,
    getFilterLabels,
    setCohorts,
  };
}
